import { useMemo } from "react";
import { Polygon, Tooltip } from "react-leaflet";
import type { LatLngTuple } from "leaflet";

import { BlastPatternFenceDTO } from "../../DTOs/Telemetry/BlastPatternFenceDTO";
import { MINIMUM_VERTICES, parseGeofencePolygon } from "./geofencePolygon";
import { StackedGroup } from "./LayerStack";
import { LAYER_RANK } from "./layerStackOrder";
import { blastPatternPathOptions, blastPatternTooltip } from "./mapStyles";
import { useBlastPatternFences } from "./useBlastPatternFences";

/**
 * Draws the LIVE blast patterns - the ones still being worked - beneath the fleet, styled by status.
 *
 * Note: a different colour from the location fences on purpose. The two answer different questions and
 * routinely overlap, since a blast pattern sits inside a pit whose own fence is already drawn. In one
 * colour the pit outline and the patterns inside it would read as one tangled shape.
 *
 * Note: "live" is decided by the SERVER (Completed = false), not here. Finished blasts accumulate forever
 * - 993 patterns against 681 not completed on the test database - so filtering client-side would mean
 * shipping the whole history down the wire to throw most of it away.
 */

type BlastPatternFenceLayerProps = {
  visible: boolean;
};

export const BlastPatternFenceLayer: React.FC<BlastPatternFenceLayerProps> = ({ visible }) => {
  const fences = useBlastPatternFences();

  const shapes = useMemo(() => fences.flatMap((fence: BlastPatternFenceDTO) => {
    const { vertices } = parseGeofencePolygon(fence.geofencePolygon);

    // Note: a polygon that will not parse is skipped rather than drawn wrong. The server logs it by
    // pattern id when inference hits the same row, so it is diagnosable there rather than here.
    if (vertices.length < MINIMUM_VERTICES) return [];

    return [{
      id: fence.patternBlastId,
      positions: vertices.map(v => [v.lat, v.lng] as LatLngTuple),
      pathOptions: blastPatternPathOptions(fence.status),
      tooltip: blastPatternTooltip(fence),
    }];
  }), [fences]);

  if (!visible) return null;

  return (
    <StackedGroup rank={LAYER_RANK.patterns} version={shapes}>
      {shapes.map(shape => (
        <Polygon key={`blast-${shape.id}`} positions={shape.positions} pathOptions={shape.pathOptions}>
          <Tooltip>{shape.tooltip}</Tooltip>
        </Polygon>
      ))}
    </StackedGroup>
  );
};

export default BlastPatternFenceLayer;
